import { useEffect, useSyncExternalStore } from "react";

/**
 * Module-level count of the modal overlays currently painted over the page.
 *
 * The native Intercom launcher renders at `z-index: 2147483000`, above every
 * surface the app can draw, so no overlay ever covers it: the bubble stays
 * visible — and clickable — on top of the dimmed page. Keeping it out of the
 * way means hiding it while an overlay is up, and that means something has to
 * know one is.
 *
 * Counted rather than flagged: overlays stack (a confirmation `AlertDialog`
 * opened from a `Dialog`), and the launcher may only come back once the last
 * one is gone.
 *
 * Fed by the shared overlay primitives themselves — `dialog.tsx`,
 * `alert-dialog.tsx`, `sheet.tsx` — so every surface built on one of them
 * counts without knowing the launcher exists. The few screens that draw their
 * own backdrop instead register with {@link useModalOverlay}.
 */
let overlayCount = 0;
const listeners = new Set<() => void>();

/**
 * Counts one overlay in; the returned callback counts it back out. Only the
 * transitions in and out of "none at all" are published — that is all the
 * snapshot says.
 */
function registerOverlay(): () => void {
  overlayCount += 1;
  if (overlayCount === 1) listeners.forEach((listener) => listener());
  return () => {
    overlayCount -= 1;
    if (overlayCount === 0) listeners.forEach((listener) => listener());
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return overlayCount > 0;
}

/** Whether a modal overlay currently holds the page. */
export function useHasModalOverlay(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Declares a modal overlay for as long as `isOpen` holds.
 *
 * For the screens that draw their own backdrop rather than going through one of
 * the shared overlay primitives — those register on their own, see
 * {@link ModalOverlayPresence}.
 */
export function useModalOverlay(isOpen: boolean): void {
  useEffect(() => (isOpen ? registerOverlay() : undefined), [isOpen]);
}
